/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */


/*
 * Layout and chrome.
 *
 * Sizing that CSS cannot express on its own: the header and warning strip
 * feed CSS custom properties the stylesheets read, and the aspect-ratio
 * helper keeps embedded media in proportion inside a box whose size only
 * JavaScript knows.
 */

/**
 * Scrolls a container to a vertical position.
 * @param {Window|Document|Element} container - Scroll target. Pass `window` or a DOM element.
 * @param {number|Element} target - Destination: a pixel offset or an element whose top is used.
 * @param {number} [offset] - Additional pixel offset added to the computed position.
 * @param {number} [duration] - When positive, requests smooth scrolling if the browser supports it.
 * @returns {void}
 */
export const scrollToY = (container, target, offset, duration) => {
    let pos;
    if (Number.isFinite(target)) {
        pos = Math.round(target);
    } else if (target) {
        const el = target;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        pos = Math.ceil(rect.top + window.scrollY);
    } else {
        return;
    }
    if (offset) pos += offset;

    const node = container;
    const wantsSmooth = duration && duration > 0;
    const canSmooth = 'scrollBehavior' in document.documentElement.style;

    // Window/document scrolling
    if (
        !node ||
        node === window ||
        node === document ||
        node === document.body ||
        node === document.documentElement
    ) {
        if (wantsSmooth && canSmooth) {
            window.scrollTo({ top: pos, behavior: 'smooth' });
        } else {
            window.scrollTo(0, pos);
        }
        return;
    }
    // Element scrolling
    if (typeof node.scrollTo === 'function') {
        if (wantsSmooth && canSmooth) {
            node.scrollTo({ top: pos, behavior: 'smooth' });
        } else {
            node.scrollTop = pos;
        }
    } else {
        node.scrollTop = pos;
    }
};

/**
 * Keeps a clipped container pinned at its scroll origin.
 *
 * An `overflow: hidden` box is not an unscrollable one: the user cannot
 * scroll it, but the browser still does, and `focus()` on something inside
 * it that lies outside its box is the usual way in. Everything positioned
 * in that box is positioned against the padding box, which moves with the
 * scroll, so the whole layer slides -- on the map that is the zoom
 * controls and the popup panel travelling with the points. The offset is
 * sticky, too: nothing scrolls it back.
 *
 * @param {Element} elem - Container to keep at its scroll origin.
 * @returns {void}
 */
export const pinScroll = (elem) => {
    if (!elem) return;
    const reset = () => {
        if (elem.scrollLeft !== 0) elem.scrollLeft = 0;
        if (elem.scrollTop !== 0) elem.scrollTop = 0;
    };
    elem.addEventListener('scroll', reset);
    reset();
};

/**
 * Reads the heights of `#header` and `.warnings--static` and writes them into
 * the CSS custom properties `--map-padding-top` and `--map-padding-bottom`.
 * Reapplied automatically on `window.resize`.
 * @returns {void}
 */
export const layout = () => {
    const layout = () => {
        const header = document.getElementById('header');
        const headerHeight = header ? header.offsetHeight : 0;
        document.documentElement.style.setProperty(
            '--map-padding-top',
            `${headerHeight}px`
        );

        const footer = document.querySelector('.warnings--static');
        const footerHeight = footer ? footer.offsetHeight : 0;
        document.documentElement.style.setProperty(
            '--map-padding-bottom',
            `${footerHeight}px`
        );
    };
    layout();
    window.addEventListener('resize', layout);
};

/**
 * Wires open/close behavior between a toggle button and a menu element.
 * Closes the menu on outside clicks and on clicks on menu links.
 * @param {Element} button - The toggle button.
 * @param {Element} menu - The menu element.
 * @returns {void}
 */
export const initMenu = (button, menu) => {
    const btn = button;
    const menuEl = menu;

    if (!btn || !menuEl) return;

    const hideOnFocusOut = (event) => {
        const t = event.target;
        const inMenuOrMenuButton = !!(
            t &&
            (t.closest('#menu') || t.closest('#menu_button'))
        );
        if (!inMenuOrMenuButton) hideMenu();
    };

    // The header's button declares `aria-expanded`, so it has to move.
    const announce = (open) => {
        if (btn.hasAttribute('aria-expanded')) {
            btn.setAttribute('aria-expanded', String(open));
        }
    };

    const showMenu = () => {
        menuEl.classList.add('is-open');
        announce(true);
        btn.addEventListener('click', hideMenu, { once: true });
        window.addEventListener('mousedown', hideOnFocusOut);
        window.addEventListener('touchstart', hideOnFocusOut, {
            passive: true
        });
    };
    const hideMenu = () => {
        window.removeEventListener('mousedown', hideOnFocusOut);
        window.removeEventListener('touchstart', hideOnFocusOut);
        menuEl.classList.remove('is-open');
        announce(false);
    };

    btn.addEventListener('click', (event) => {
        event.preventDefault();
        const isOpen = menuEl.classList.contains('is-open');
        if (!isOpen) showMenu();
        else hideMenu();
    });

    // close on menu link click
    menuEl.querySelectorAll('a[href]').forEach((a) => {
        a.addEventListener('click', hideMenu);
    });
};

/**
 * Maintains a fixed aspect ratio for an element, reapplied on `window.resize`.
 * @param {Element} elem - The element to resize.
 * @param {number} ratio - Width-to-height ratio (e.g. `16/9`).
 * @param {'width'|'height'|'both'} which - Which dimension to adjust.
 * @param {Element} [container] - When provided, sizes `elem` relative to
 *   the container. When omitted, sizes `elem` relative to itself.
 * @returns {void}
 */
export const keepAspectRatio = (elem, ratio, which, container) => {
    const el = elem;

    const keepRatio = () => {
        if (container === undefined) {
            if (which === 'width') {
                const h = el.clientWidth / ratio;
                el.style.height = `${h}px`;
            } else if (which === 'height') {
                const w = el.clientHeight * ratio;
                el.style.width = `${w}px`;
            }
        } else {
            const c = container;
            const containerWidth = c.clientWidth;
            const containerHeight = c.clientHeight;
            if (which === 'width') {
                el.style.width = `${containerWidth}px`;
                el.style.height = `${Math.round(containerWidth / ratio)}px`;
            } else if (which === 'height') {
                el.style.width = `${Math.round(containerHeight * ratio)}px`;
                el.style.height = `${containerHeight}px`;
            } else if (which === 'both') {
                if (
                    el.clientWidth / el.clientHeight >
                    containerWidth / containerHeight
                ) {
                    el.style.width = `${containerWidth}px`;
                    el.style.height = `${Math.round(containerWidth / ratio)}px`;
                } else {
                    el.style.width = `${Math.round(containerHeight * ratio)}px`;
                    el.style.height = `${containerHeight}px`;
                }
            }
        }
    };
    keepRatio();
    window.addEventListener('resize', keepRatio);
};
